


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type {
  HokkaidoAirportReportCar,
  HokkaidoAirportReport,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportReportBaseArrivalTsUpdateParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import hokkaidoAirportCarApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_car';
import useSpeech from '@/composables/useSpeech';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { useRoute } from '@/hooks/useRoute';
import { roundToIntervalMinutes } from '@/lib/dateTimeHelper';
import { REPORT_TS_ROUND_MINUTES } from '@/consts/externalReports/hokkaidoAirport';

interface PostWorkState {
  showConfirmBaseArrivalModal: boolean;
  hokkaidoAirportReport: HokkaidoAirportReport;
}
export default defineComponent({
  props: {
    hokkaidoAirportCar: {
      type: Object as PropType<HokkaidoAirportReportCar>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
  },
  emits: ['editContentAndRoad', 'showErrorOnCommon', 'logoutAfterBaseArrival'],
  setup(props, { emit }) {
    const state = reactive<PostWorkState>({
      showConfirmBaseArrivalModal: false,
      hokkaidoAirportReport: props.hokkaidoAirportCar.report,
    });
    const { doSpeech } = useSpeech();
    const { router } = useRoute();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const spLabels = computed<HokkaidoAirportLabelSp>(() => {
      return props.userSettings.sp;
    });

    const baseArrival = async() => {
      state.showConfirmBaseArrivalModal = false;
      const baseArrivalTime = roundToIntervalMinutes(new Date(), REPORT_TS_ROUND_MINUTES);
      const params: HokkaidoAirportReportBaseArrivalTsUpdateParams = {
        base_arrival_ts: baseArrivalTime,
      };
      await doRequest(async() => {
        state.hokkaidoAirportReport = await hokkaidoAirportReportApi.updateBaseArrival(
          state.hokkaidoAirportReport.id,
          params,
        );
        await hokkaidoAirportCarApi.updateReportId(
          props.hokkaidoAirportCar.id,
          { current_report_id: null },
        );
      }, '基地到着に失敗しました。再度操作を行ってください。');
      doSpeech(spLabels.value.message.confirm_complete_operation);
      emit('logoutAfterBaseArrival');
    };

    const editContentAndRoad = () => {
      emit('editContentAndRoad');
    };

    onMounted(() => {
      if (state.hokkaidoAirportReport.id === 0) {
        router.replace({name: 'Login'});
      }
    });

    return {
      state,
      isRequesting,
      spLabels,
      editContentAndRoad,
      baseArrival,
    };
  },
});
